import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const ServicesPage = ({ seoSettings }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 lg:pt-[118px] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B7355] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 lg:pt-[118px] min-h-screen">
      <SEO 
        title={`Services - ${seoSettings?.seo_title || 'Mélange Mobile Bar'}`}
        description={seoSettings?.seo_description}
        keywords={seoSettings?.seo_keywords}
        ogImage={seoSettings?.seo_og_image}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F7F4F0] to-[#F7F4F0] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="services-page-title">
            Our Services
          </h1>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Professional bartending packages tailored to your event needs
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={service.id}
                data-testid={`service-item-${index}`}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover-lift"
              >
                <div className="h-48 bg-gradient-to-br from-[#D4C4B0] to-[#8B7355] flex items-center justify-center">
                  <div className="text-7xl">🍹</div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.name}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{service.description}</p>
                  <div className="mb-4">
                    <p className="text-3xl font-bold text-[#8B7355]">{service.price}</p>
                  </div>
                  <div className="space-y-2 mb-6">
                    {service.features && service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start">
                        <Check className="text-green-600 mr-2 flex-shrink-0 mt-1" size={16} />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/contact">
                    <Button className="w-full btn-primary" data-testid={`book-service-button-${index}`}>
                      Book This Service
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {services.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-gray-600">No services available at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#F7F4F0] to-[#F7F4F0]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4" data-testid="custom-package-title">
            Need a Custom Package?
          </h2>
          <p className="text-base text-gray-600 mb-8">
            We can create a personalized package to fit your specific needs and budget
          </p>
          <Link to="/contact">
            <Button size="lg" className="btn-primary" data-testid="contact-us-button">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
